// CC-35 — UI Batch matching devis/facture installateur
// Drag-drop multi-PDF → POST /api/metier/installateur-docs/batch-match
// Vision Claude extrait + matche à un dossier OH (TEF only).
import { useState, useCallback, useRef, useMemo } from "react";
import {
  Upload, FileText, Loader2, CheckCircle2, XCircle,
  ExternalLink, Trash2, FileSearch, AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { OHGrid } from "../../components/ui/oh-grid";
import { enteteTenant } from "../../utils/enteteTenant";
import { tenantResolu } from "../../utils/tenantResolu";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 50;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
});

const TYPE_BADGE = {
  devis: { label: "Devis", color: "blue" },
  facture: { label: "Facture", color: "green" },
  inconnu: { label: "Inconnu", color: "amber" },
};

function TypeCell({ value }) {
  const meta = TYPE_BADGE[value] || TYPE_BADGE.inconnu;
  return (
    <Badge
      className="text-[10px] px-2 py-0"
      style={{
        background: `var(--oh-stat-${meta.color}-bg)`,
        color: `var(--oh-stat-${meta.color}-text)`,
        border: `1px solid var(--oh-stat-${meta.color}-border)`,
      }}
      data-testid={`type-${value}`}
    >
      {meta.label}
    </Badge>
  );
}

function MatchCell({ row, onOpen }) {
  if (!row.matched) {
    return (
      <span className="inline-flex items-center gap-1 text-xs"
        style={{ color: "var(--oh-text-muted)" }}>
        <XCircle className="w-3.5 h-3.5" /> Non matché
      </span>
    );
  }
  const ref = row.matched_dossier_reference || "—";
  const score = row.match_score ? `${Math.round(row.match_score * 100)}%` : "";
  return (
    <button
      onClick={() => onOpen?.(row.matched_dossier_id)}
      className="inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
      style={{ color: "var(--oh-stat-green-text)" }}
      data-testid="open-matched-dossier"
      aria-label={`Ouvrir dossier ${ref}`}
    >
      <CheckCircle2 className="w-3.5 h-3.5" />
      <span className="font-mono">{ref}</span>
      {score && <span style={{ color: "var(--oh-text-muted)" }}>({score})</span>}
      <ExternalLink className="w-3 h-3" />
    </button>
  );
}

function MoneyCell({ value }) {
  if (value == null) return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
  return <span>{Number(value).toLocaleString("fr-FR")} €</span>;
}

export default function InstallateurDocsBatch({ onOpenDossier }) {
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [report, setReport] = useState(null); // { total, matched_count, unmatched_count, reports: [] }
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    const pdfs = Array.from(incoming).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    if (!pdfs.length) {
      toast.error("Aucun PDF dans la sélection");
      return;
    }
    setFiles((prev) => {
      const combined = [...prev, ...pdfs];
      if (combined.length > MAX_FILES) {
        toast.warning(`Limite ${MAX_FILES} PDFs : seuls les ${MAX_FILES} premiers seront analysés`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleSelect = useCallback((e) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = ""; // reset pour permettre re-sélection même fichier
  }, [addFiles]);

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
    setReport(null);
  }, []);

  const submit = useCallback(async () => {
    if (!files.length) return toast.error("Sélectionnez au moins un PDF");
    setProcessing(true);
    setReport(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(
        `${API}/api/metier/installateur-docs/batch-match`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), ...enteteTenant(tenantResolu()) },
          credentials: "include",
          body: fd,
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setReport(data);
      toast.success(
        `${data.matched_count}/${data.total} PDF(s) matché(s) à un dossier`,
      );
    } catch (e) {
      toast.error(`Erreur batch : ${e.message || e}`);
    } finally {
      setProcessing(false);
    }
  }, [files]);

  // ─── Tableau résultats ────────────────────────────────────────────────
  const rows = useMemo(() => {
    if (!report?.reports) return [];
    return report.reports.map((r, idx) => {
      const ex = r.extraction || {};
      return {
        id: `row-${idx}`,
        filename: r.filename,
        error: r.error || null,
        matched: r.matched,
        type: ex.type || "inconnu",
        numero: ex.numero || "",
        date: ex.date || "",
        montant_ht: ex.montant_ht ?? null,
        montant_ttc: ex.montant_ttc ?? null,
        installateur_nom: ex.installateur_nom || "",
        installateur_siret: ex.installateur_siret || "",
        client_nom: ex.client_nom || "",
        matched_dossier_id: ex.matched_dossier_id || null,
        matched_dossier_reference: ex.matched_dossier_reference || null,
        match_method: ex.match_method || null,
        match_score: ex.match_score ?? null,
      };
    });
  }, [report]);

  const columns = useMemo(() => [
    {
      key: "filename", label: "Fichier", sortable: true,
      render: (v, row) => (
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--oh-text-muted)" }} />
          <span className="truncate text-xs" title={v}>{v}</span>
          {row.error && (
            <span className="inline-flex items-center gap-1 text-[10px]"
              style={{ color: "var(--oh-stat-red-text)" }} title={row.error}>
              <AlertTriangle className="w-3 h-3" /> erreur
            </span>
          )}
        </div>
      ),
    },
    { key: "type", label: "Type", sortable: true,
      render: (v) => <TypeCell value={v} /> },
    { key: "numero", label: "N°", sortable: true,
      render: (v) => <span className="font-mono text-xs">{v || "—"}</span> },
    { key: "date", label: "Date", sortable: true,
      render: (v) => <span className="text-xs">{v || "—"}</span> },
    { key: "montant_ht", label: "HT", sortable: true,
      render: (v) => <MoneyCell value={v} /> },
    { key: "montant_ttc", label: "TTC", sortable: true,
      render: (v) => <MoneyCell value={v} /> },
    { key: "installateur_nom", label: "Installateur", sortable: true,
      render: (v) => <span className="text-xs">{v || "—"}</span> },
    { key: "client_nom", label: "Client extrait", sortable: true,
      render: (v) => <span className="text-xs">{v || "—"}</span> },
    { key: "matched_dossier_reference", label: "Dossier OH",
      render: (_v, row) => <MatchCell row={row} onOpen={onOpenDossier} /> },
  ], [onOpenDossier]);

  const stats = useMemo(() => {
    if (!report) return [];
    return [
      { key: "total", label: "Total", value: report.total, color: "blue", filter: null },
      { key: "matched", label: "Matchés", value: report.matched_count, color: "green",
        filter: (row) => row.matched },
      { key: "unmatched", label: "Non matchés", value: report.unmatched_count, color: "amber",
        filter: (row) => !row.matched },
    ];
  }, [report]);

  return (
    <div className="space-y-6" data-testid="installateur-docs-batch">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 rounded-full" style={{ background: "var(--oh-hub-operation)" }} />
        <h2 className="text-lg font-bold" style={{ color: "var(--oh-text)" }}>
          Devis & factures installateur — analyse batch
        </h2>
        <Badge className="text-xs px-2 py-0.5"
          style={{
            background: "var(--oh-stat-cyan-bg)",
            color: "var(--oh-stat-cyan-text)",
            border: "1px solid var(--oh-stat-cyan-border)",
          }}>
          CC-35 · Vision
        </Badge>
      </div>

      <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
        Glisse jusqu&apos;à {MAX_FILES} PDFs (devis ou factures installateur).
        Chaque fichier est analysé par Claude Vision puis matché à un dossier
        OH par référence ou nom client. Aucune écriture en base — outil de
        rapprochement uniquement.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className="rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors"
        style={{
          borderColor: dragOver ? "var(--oh-hub-operation)" : "var(--oh-border)",
          background: dragOver ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)",
        }}
        data-testid="dropzone"
        role="button"
        aria-label="Zone de dépôt de fichiers PDF"
      >
        <Upload className="w-10 h-10 mx-auto mb-3"
          style={{ color: "var(--oh-text-muted)" }} />
        <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
          Glisse des PDFs ici ou clique pour sélectionner
        </p>
        <p className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>
          {files.length}/{MAX_FILES} fichier(s) sélectionné(s)
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={handleSelect}
          className="hidden"
          aria-label="Sélecteur de fichiers PDF"
        />
      </div>

      {/* Liste fichiers + actions */}
      {files.length > 0 && (
        <div className="rounded-lg p-3 space-y-2"
          style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium" style={{ color: "var(--oh-text-secondary)" }}>
              Sélection
            </span>
            <Button variant="ghost" size="sm" onClick={clearAll}
              className="h-7 text-xs" data-testid="clear-files">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Vider
            </Button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`}
                className="flex items-center gap-2 text-xs py-1 px-2 rounded"
                style={{ background: "var(--oh-bg-surface)" }}>
                <FileText className="w-3.5 h-3.5 flex-shrink-0"
                  style={{ color: "var(--oh-text-muted)" }} />
                <span className="truncate flex-1" style={{ color: "var(--oh-text)" }}>
                  {f.name}
                </span>
                <span style={{ color: "var(--oh-text-muted)" }}>
                  {(f.size / 1024).toFixed(0)} ko
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                  aria-label={`Retirer ${f.name}`}
                  className="p-0.5 rounded hover:bg-red-500/10"
                >
                  <XCircle className="w-3.5 h-3.5"
                    style={{ color: "var(--oh-stat-red-text)" }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bouton analyser */}
      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={submit}
          disabled={!files.length || processing}
          data-testid="submit-batch"
          aria-label="Lancer l'analyse Vision sur les PDFs sélectionnés"
        >
          {processing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours…</>
          ) : (
            <><FileSearch className="w-4 h-4 mr-2" /> Analyser ({files.length})</>
          )}
        </Button>
      </div>

      {/* Résultats */}
      {report && (
        <OHGrid
          data={rows}
          columns={columns}
          stats={stats}
          selectable={false}
          pagination={true}
          pageSize={25}
          loading={false}
          searchPlaceholder="Filtrer par fichier, installateur, client…"
          searchFilter={(row, q) => (
            (row.filename || "").toLowerCase().includes(q) ||
            (row.installateur_nom || "").toLowerCase().includes(q) ||
            (row.client_nom || "").toLowerCase().includes(q) ||
            (row.matched_dossier_reference || "").toLowerCase().includes(q)
          )}
          defaultSortKey="matched"
          defaultSortDir="desc"
          emptyMessage="Aucun résultat"
          testIdPrefix="installateur-docs-batch"
        />
      )}
    </div>
  );
}
